import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { NavLink, useNavigate } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, CircleX } from 'lucide-react';
import {
  Form,
  FormControl,
  FormItem,
  FormMessage,
  Input,
  Button,
  Card,
  FormLabel,
} from '@predoc/cow';
import { toast } from 'sonner';

import { Footer } from './Footer';
import { loginSchema, type LoginFormValues, type MfaStep } from '../types';
import {
  AUTH_ERRORS,
  AUTH_SUCCESS,
  AUTH_PATHS,
  AUTH_SESSION_KEYS,
} from '../constants';

import { PasswordInput } from '@/components';
import {
  useSignIn,
  useFetchUserInfo,
  signInWithSsoRedirect,
} from '../hooks/authApi';
import { useMfa } from '../MfaContext';
import { fetchMfaSetupGuard } from '../hooks/useMfaSetupGuard';
import { usePageTitle, useBranding } from '@/hooks';

type LoginStep = 'email' | 'password';

export const Login = () => {
  usePageTitle('Login');
  const brand = useBranding();
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>('email');
  const { mutateAsync, isPending, error, reset: resetSignIn } = useSignIn();
  const { mutateAsync: fetchUserInfo, isPending: isFetchingUserInfo } =
    useFetchUserInfo();
  const {
    setTempCredentials,
    setAuthEmail,
    setOrganizationMfaEnabled,
    setMfaVerificationMode,
  } = useMfa();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    reValidateMode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const emailValue = useWatch({ control: form.control, name: 'email' });

  const handleFormChange = () => {
    // Clear form errors on change
  };

  const handleContinue = async () => {
    const email = form.getValues('email').trim();
    form.setValue('email', email);
    const valid = await form.trigger('email');
    if (!valid || form.formState.errors.email) {
      return;
    }
    let userInfo;
    try {
      userInfo = await fetchUserInfo(email);
    } catch (err) {
      console.error('getUserInfo failed:', err);
      toast.error(
        'Unable to verify your login requirements. Please try again.',
      );
      return;
    }
    const extAuthProvider = userInfo?.extAuthProvider?.trim();
    if (extAuthProvider) {
      setAuthEmail(null);
      setOrganizationMfaEnabled(false);
      setMfaVerificationMode(null);
      try {
        await signInWithSsoRedirect(extAuthProvider);
        toast.info(`Redirecting to ${extAuthProvider} for authentication...`);
        return;
      } catch (err) {
        console.error(
          `SSO redirect failed: ${err instanceof Error ? err.message : String(err)}`,
          err,
        );
        toast.error('SSO sign-in failed. Please try again.');
        return;
      }
    }
    if (userInfo?.email) {
      form.setValue('email', userInfo.email);
    }
    const normalizedEmail = userInfo?.email ?? email;
    const isOrganizationMfaEnabled = userInfo?.enableMfa ?? false;
    setAuthEmail(normalizedEmail);
    setOrganizationMfaEnabled(isOrganizationMfaEnabled);
    setStep('password');
    form.setValue('password', '');
    form.clearErrors('password');
    resetSignIn();
  };

  const handleBack = () => {
    setStep('email');
    form.setValue('password', '');
    form.clearErrors();
    resetSignIn();
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (step === 'email') {
      e.preventDefault();
      await handleContinue();
      return;
    }
    await form.handleSubmit(handleSubmit)(e);
  };

  const handleSubmit = async (values: LoginFormValues) => {
    try {
      const result = await mutateAsync({
        email: values.email,
        password: values.password,
      });

      // challenge names as below documentation
      // https://docs.amplify.aws/react/build-a-backend/auth/connect-your-frontend/multi-step-sign-in/
      const mfaStep = result.nextStep.signInStep as MfaStep;
      if (
        mfaStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
        mfaStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' ||
        mfaStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
      ) {
        // Store credentials for resending MFA code
        setTempCredentials({
          username: values.email,
          password: values.password,
        });
        toast.success(AUTH_SUCCESS.VERIFICATION_CODE_SENT);
        navigate(AUTH_PATHS.MFA_VERIFICATION);
      } else if (mfaStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        sessionStorage.setItem(AUTH_SESSION_KEYS.NEW_PASSWORD_REQUIRED, 'true');
        navigate(AUTH_PATHS.NEW_PASSWORD_REQUIRED);
      } else {
        sessionStorage.removeItem(AUTH_SESSION_KEYS.NEW_PASSWORD_REQUIRED);

        const mfaGuard = await fetchMfaSetupGuard(values.email);

        if (mfaGuard.setupRequired) {
          setMfaVerificationMode(null);
          navigate(AUTH_PATHS.MFA_SETUP_PHONE);
          return;
        }

        setMfaVerificationMode(null);
        navigate(AUTH_PATHS.HOME);
      }
    } catch {
      toast.error('Login failed.');
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-between bg-gray-50">
      <div className="flex w-full flex-1 items-center justify-center">
        <Card className="flex w-full max-w-[960px] flex-row overflow-hidden p-0">
          <div
            className={`flex w-1/2 items-center justify-center ${brand.loginBgClass} p-16`}
          >
            <img
              src={brand.loginLogo}
              alt={`${brand.name} Logo`}
              className="w-48"
            />
          </div>

          <div className="flex flex-1 items-center justify-center p-16 pb-24">
            <div className="w-full max-w-[360px] space-y-5">
              <div className="space-y-2 text-center">
                <h1 className="text-3xl font-semibold tracking-tight">Login</h1>
                <p className="text-muted-foreground text-sm">
                  {step === 'email'
                    ? 'Enter your email to login.'
                    : 'Enter your password to login.'}
                </p>
              </div>

              <Form {...form}>
                <form
                  onSubmit={handleFormSubmit}
                  onChange={handleFormChange}
                  className="space-y-6"
                  noValidate
                >
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="ex. jdoe@email.com"
                        readOnly={step === 'password'}
                        className={
                          step === 'password'
                            ? 'bg-muted cursor-pointer'
                            : undefined
                        }
                        onFocus={() => {
                          if (step === 'password') handleBack();
                        }}
                        {...form.register('email', {
                          onChange: () => form.clearErrors('email'),
                        })}
                        aria-label="Email input"
                      />
                    </FormControl>
                    {step === 'email' && (
                      <FormMessage>
                        {form.formState.errors.email?.message}
                      </FormMessage>
                    )}
                  </FormItem>

                  <AnimatePresence initial={false}>
                    {step === 'password' && (
                      <motion.div
                        key="password-step"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="space-y-6 overflow-hidden"
                      >
                        <FormItem>
                          <FormLabel className="mb-2 flex w-full items-center justify-between">
                            Password
                            <NavLink
                              to={AUTH_PATHS.FORGOT_PASSWORD}
                              className="text-muted-foreground p-0 text-sm underline hover:text-gray-900"
                            >
                              Forgot password?
                            </NavLink>
                          </FormLabel>
                          <FormControl>
                            <PasswordInput
                              {...form.register('password')}
                              aria-label="Password input"
                            />
                          </FormControl>
                        </FormItem>

                        {(error ||
                          form.formState.errors.email ||
                          form.formState.errors.password) && (
                          <div className="flex items-center justify-center gap-1.5">
                            <CircleX className="text-destructive h-4 w-4 shrink-0" />
                            <p className="text-destructive">
                              {AUTH_ERRORS.INVALID_CREDENTIALS}
                            </p>
                          </div>
                        )}

                        <Button
                          type="submit"
                          className="h-11 w-full"
                          disabled={!form.formState.isValid || isPending}
                          loading={isPending}
                        >
                          {isPending ? 'Logging in...' : 'Login'}
                        </Button>

                        <div className="flex w-full items-center justify-between">
                          <Button
                            type="button"
                            variant="ghost"
                            className="text-muted-foreground gap-2 p-0 font-medium hover:bg-transparent hover:text-gray-900"
                            onClick={handleBack}
                          >
                            <ArrowLeft className="h-4 w-4" />
                            Back
                          </Button>
                          <p className="text-muted-foreground text-sm">
                            Don&apos;t have an account?{' '}
                            <Button
                              variant="link"
                              className="text-muted-foreground p-0 underline"
                              asChild
                            >
                              <a
                                href="https://www.predoc.ai/get-started"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Get Started
                              </a>
                            </Button>
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {step === 'email' && (
                    <Button
                      type="button"
                      className="h-11 w-full"
                      disabled={!emailValue?.trim() || isFetchingUserInfo}
                      loading={isFetchingUserInfo}
                      onClick={handleContinue}
                    >
                      {isFetchingUserInfo ? 'Checking...' : 'Continue'}
                    </Button>
                  )}
                </form>
              </Form>

              {step === 'email' && (
                <div className="text-center">
                  <p className="text-muted-foreground text-sm">
                    Don&apos;t have an account?{' '}
                    <Button
                      variant="link"
                      className="text-muted-foreground p-0 underline"
                      asChild
                    >
                      <a
                        href="https://www.predoc.ai/get-started"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Get Started
                      </a>
                    </Button>
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};
